import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  deliverInvitationWelcome,
  getInvitationWelcomes,
  type InvitationWelcomeDto,
  serverErrorMessage,
  suppressInvitationWelcome,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import { LIVE_POLL_MS } from '../../lib/poll.js';

/**
 * batch-411 (ROADMAP P1-28, slice 1) - the Welcomes tab of the connection requests view.
 *
 * An unattended accept (the backlog drain, or the accept that follows an assessment) sends no
 * welcome while auto-welcome is off; the worker leaves ONE held welcome per such accept. Each shows
 * here with its text editable. The operator reads it, then sends it or chooses not to - nothing on
 * this page sends by itself, and a send is one deliberate click on one card.
 *
 * A send the provider did not confirm (a 5xx or a timeout) is shown as exactly that: the message may
 * already be on LinkedIn, so the card says to check the conversation first. Sending again is then the
 * operator's call, never an automatic retry.
 */

const NO_WELCOMES: InvitationWelcomeDto[] = [];

export function HeldWelcomes({ sendArmed }: { sendArmed: boolean }) {
  const [showHistory, setShowHistory] = useState(false);
  const open = useQuery({
    queryKey: ['invitation-welcomes', 'open'],
    queryFn: ({ signal }) => getInvitationWelcomes('open', { signal }),
    refetchInterval: LIVE_POLL_MS,
  });
  const history = useQuery({
    queryKey: ['invitation-welcomes', 'history'],
    queryFn: ({ signal }) => getInvitationWelcomes('history', { signal }),
    enabled: showHistory,
  });
  const rows = open.data?.welcomes ?? NO_WELCOMES;

  return (
    <div className="jc-canvas" data-testid="held-welcomes">
      <div className="jc-canvas-head">
        <h2>Welcomes waiting</h2>
      </div>
      <p className="jc-hint">
        Accepted without a welcome. Read each one, edit it if you like, then send it or skip it.
        Nothing here is sent unless you click Send welcome.
      </p>

      {open.isLoading ? (
        <p className="jc-hint">Loading…</p>
      ) : open.isError ? (
        <p className="jc-failed" role="alert">
          Could not load the welcomes.{' '}
          <button type="button" className="jc-btn" onClick={() => void open.refetch()}>
            Retry
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="queue-canvas-empty" data-testid="held-welcomes-empty">
          No welcomes waiting. An accept made without you leaves one here.
        </p>
      ) : (
        rows.map((w) => <WelcomeCard key={w.id} welcome={w} sendArmed={sendArmed} />)
      )}

      <div className="jc-actions">
        <button
          type="button"
          className="jc-btn"
          data-testid="held-welcomes-history-toggle"
          onClick={() => setShowHistory((v) => !v)}
        >
          {showHistory ? 'Hide sent and skipped' : 'Show sent and skipped'}
        </button>
      </div>
      {showHistory ? (
        <ul className="eng-history" data-testid="held-welcomes-history">
          {history.isLoading ? (
            <li>Loading…</li>
          ) : (history.data?.welcomes ?? NO_WELCOMES).length === 0 ? (
            <li>Nothing sent or skipped yet.</li>
          ) : (
            (history.data?.welcomes ?? NO_WELCOMES).map((h) => (
              <li key={h.id}>
                <span className="jc-kind">
                  {h.status === 'sent'
                    ? h.trigger === 'auto'
                      ? 'sent automatically'
                      : 'sent'
                    : 'skipped'}
                </span>
                <strong>{h.sender_name ?? 'A person'}</strong>
                {h.finalized_at ? <time>{formatRelative(h.finalized_at)}</time> : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * batch-420 (P1-28 piece B) - why a welcome is here, for the two states the automatic welcome adds: a
 * SCHEDULED one (COMMS will send it in business hours unless the operator acts first) and one the
 * automatic path HELD back (its line failed the wording check, or a re-check before sending failed).
 */
function automaticNote(w: InvitationWelcomeDto): string | null {
  if (w.status === 'scheduled') {
    return w.welcome_text == null
      ? 'Scheduled to send automatically (Mon-Fri, 09:00-18:00 GST). Its one personal line is still being written.'
      : 'Scheduled to send automatically (Mon-Fri, 09:00-18:00 GST). Send it now, or choose not to send it.';
  }
  if (w.status === 'held' && w.error && /^(auto|line): /.test(w.error)) {
    return `Held back from the automatic welcome: ${w.error.replace(/^(auto|line): /, '')}`;
  }
  return null;
}

/** What the last failed attempt means for the operator, in plain words. */
function failureNote(w: InvitationWelcomeDto): string | null {
  if (w.status === 'attempted') {
    return 'A send was started but never confirmed. Check the conversation on LinkedIn - it cannot be sent again from here.';
  }
  if (w.status !== 'failed') return null;
  return w.delivery_unknown
    ? 'The last attempt was not confirmed by LinkedIn, so the message may already be there. Check the conversation before sending it again.'
    : 'The last attempt failed and nothing was sent. You can try again.';
}

function WelcomeCard({
  welcome: w,
  sendArmed,
}: {
  welcome: InvitationWelcomeDto;
  sendArmed: boolean;
}) {
  const qc = useQueryClient();
  // `null` = follow the stored text; a string = the operator's edit.
  const [override, setOverride] = useState<string | null>(null);
  const text = override ?? w.welcome_text ?? '';
  const [err, setErr] = useState<string | null>(null);
  // batch-413 - a screened welcome needs one deliberate step before its Send button appears: either
  // "Review before sending" or switching to the neutral referral wording.
  const [screenSeen, setScreenSeen] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [done, setDone] = useState<'sent' | 'skipped' | null>(null);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['invitation-welcomes'] });
    void qc.invalidateQueries({ queryKey: ['invitation-welcome-count'] });
  };

  const deliver = useMutation({
    mutationFn: () => deliverInvitationWelcome(w.id, text.trim() || undefined),
    onMutate: () => setErr(null),
    onSuccess: () => {
      setDone('sent');
      invalidate();
    },
    onError: (e) => {
      setErr(serverErrorMessage(e, 'the welcome could not be sent'));
      invalidate();
    },
  });
  const skip = useMutation({
    mutationFn: () => suppressInvitationWelcome(w.id),
    onMutate: () => setErr(null),
    onSuccess: () => {
      setDone('skipped');
      invalidate();
    },
    onError: (e) => {
      setConfirmSkip(false);
      setErr(serverErrorMessage(e, 'could not skip this welcome'));
      invalidate();
    },
  });

  const note = failureNote(w);
  const autoNote = automaticNote(w);
  // batch-420 - a scheduled welcome is the operator's to take over too: sending it now (their text, as
  // written) or not sending it; either way the automatic path can then never send it.
  const actionable = w.status === 'held' || w.status === 'failed' || w.status === 'scheduled';
  const busy = deliver.isPending || skip.isPending;
  const screened = w.screen?.screened === true;
  const referral = w.referral_draft ?? null;
  const awaitingScreenStep = screened && !screenSeen;

  return (
    <div className="jc-canvas" data-testid="held-welcome-card">
      <div className="jc-canvas-head">
        <h3>{w.sender_name ?? 'A person'}</h3>
        <span className="jc-hint">Accepted {formatRelative(w.created_at)}</span>
      </div>
      {w.sender_headline ? <p className="jc-hint">{w.sender_headline}</p> : null}
      {w.sender_company ? <p className="jc-row-meta">🏢 {w.sender_company}</p> : null}

      {screened ? (
        <div className="jc-hint" role="note" data-testid="held-welcome-screen">
          <strong>Held by the regulated / ADGM-contact screen.</strong>
          <ul>
            {(w.screen?.reasons ?? []).map((r) => (
              <li key={r.code}>{r.label}</li>
            ))}
          </ul>
          {referral && actionable && done === null && text !== referral ? (
            <button
              type="button"
              className="jc-btn"
              data-testid="held-welcome-use-referral"
              onClick={() => {
                setOverride(referral);
                setScreenSeen(true);
              }}
              disabled={busy}
            >
              Use neutral referral wording
            </button>
          ) : null}
        </div>
      ) : null}

      <textarea
        value={text}
        onChange={(e) => setOverride(e.target.value)}
        rows={3}
        className="jc-draft"
        aria-label={`Welcome message for ${w.sender_name ?? 'this person'}`}
        disabled={!actionable || done !== null}
      />

      {done ? (
        <span className="jc-sent" data-testid="held-welcome-done">
          {done === 'sent' ? 'Sent ✓' : 'Skipped ✓'}
        </span>
      ) : actionable ? (
        <div className="jc-actions">
          {sendArmed ? (
            awaitingScreenStep ? (
              <button
                type="button"
                className="jc-btn"
                data-testid="held-welcome-review"
                onClick={() => setScreenSeen(true)}
                disabled={busy}
              >
                Review before sending
              </button>
            ) : (
              <button
                type="button"
                className="jc-btn jc-send"
                data-testid="held-welcome-send"
                onClick={() => deliver.mutate()}
                disabled={busy || !text.trim()}
              >
                {deliver.isPending
                  ? 'Sending…'
                  : w.status === 'failed'
                    ? 'Send again'
                    : w.status === 'scheduled'
                      ? 'Send now'
                      : 'Send welcome'}
              </button>
            )
          ) : (
            <span className="jc-hint">Sending is not enabled - review only.</span>
          )}
          {confirmSkip ? (
            <>
              <button
                type="button"
                className="jc-btn"
                data-testid="held-welcome-skip-confirm"
                onClick={() => skip.mutate()}
                disabled={busy}
              >
                {skip.isPending ? 'Skipping…' : "Yes, don't send"}
              </button>
              <button
                type="button"
                className="jc-btn"
                onClick={() => setConfirmSkip(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="jc-btn"
              data-testid="held-welcome-skip"
              onClick={() => setConfirmSkip(true)}
              disabled={busy}
            >
              Don't send
            </button>
          )}
        </div>
      ) : null}

      {autoNote ? (
        <p className="jc-hint" data-testid="held-welcome-auto-note">
          {autoNote}
        </p>
      ) : null}
      {note ? (
        <p className="jc-failed" data-testid="held-welcome-note">
          {note}
        </p>
      ) : null}
      {err ? (
        <p className="jc-failed" role="alert" data-testid="held-welcome-error">
          {err}
        </p>
      ) : null}
    </div>
  );
}
